import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/StatsModal.css';

const StatsModal = ({ isOpen, onClose, currentGuessCount = null }) => {
  const { isAuthenticated, stats, refreshStats } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      setLoading(true);
      refreshStats().finally(() => setLoading(false));
    }
  }, [isOpen, isAuthenticated, refreshStats]); // refreshStats is now memoized in AuthContext

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target.className === 'stats-modal-backdrop') {
      onClose();
    }
  };

  const renderDistributionBar = (count, maxCount, guessNum) => {
    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
    const isCurrentGuess = currentGuessCount === guessNum;

    return (
      <div className="distribution-row" key={guessNum}>
        <div className="distribution-label">{guessNum}</div>
        <div className="distribution-bar-container">
          <div 
            className={`distribution-bar ${isCurrentGuess ? 'current' : ''}`}
            style={{ width: `${Math.max(percentage, count > 0 ? 5 : 0)}%` }}
          >
            {count > 0 && <span className="distribution-count">{count}</span>}
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    if (!isAuthenticated) {
      return (
        <div className="stats-content">
          <div className="stats-message">
            <p>📊 Sign in to track your stats across devices!</p>
            <p className="stats-submessage">
              Keep track of your games played, win percentage, streaks, and more.
            </p>
          </div>
        </div>
      );
    }

    if (loading || !stats) {
      return (
        <div className="stats-content">
          <div className="stats-loading">Loading stats...</div>
        </div>
      );
    }

    const distribution = stats.guess_distribution || {};
    const guessData = [
      { num: 1, count: distribution.one || 0 },
      { num: 2, count: distribution.two || 0 },
      { num: 3, count: distribution.three || 0 },
      { num: 4, count: distribution.four || 0 },
      { num: 5, count: distribution.five || 0 },
      { num: 6, count: distribution.six || 0 },
    ];
    
    const maxCount = Math.max(...guessData.map(d => d.count), 1);

    return (
      <div className="stats-content">
        <div className="stats-summary">
          <div className="stat-item">
            <div className="stat-value">{stats.total_games}</div>
            <div className="stat-label">Played</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.win_percentage}%</div>
            <div className="stat-label">Win %</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.current_streak}</div>
            <div className="stat-label">Streak</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.best_streak}</div>
            <div className="stat-label">Max</div>
          </div>
        </div>

        <div className="stats-distribution">
          <h3>Guess Distribution</h3>
          <div className="distribution-chart">
            {guessData.map(data => renderDistributionBar(data.count, maxCount, data.num))}
            {distribution.failed > 0 && (
              <div className="distribution-row">
                <div className="distribution-label">X</div>
                <div className="distribution-bar-container">
                  <div 
                    className="distribution-bar failed"
                    style={{ width: `${Math.max((distribution.failed / maxCount) * 100, 5)}%` }}
                  >
                    <span className="distribution-count">{distribution.failed}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="stats-modal-backdrop" onClick={handleBackdropClick}>
      <div className="stats-modal">
        <button className="stats-modal-close" onClick={onClose}>×</button>
        
        <h2 className="stats-title">📊 Your Stats</h2>
        
        {renderStats()}
      </div>
    </div>
  );
};

export default StatsModal;
